import { useRef, useState } from "react"
import { Markdown } from "../../nira2/ds/Markdown"
import type { SimEngine } from "../engine"
import { CONTINUE_OPTIONS } from "./messages"
import "./checkpointCard.css"

export interface CheckpointCardPayload { stage: string; summary_markdown: string; options: string[] }

/** True when the agent's note says more than one plain sentence (a list, a table, a second paragraph or sentence). */
export function noteHasMore(summary: string): boolean {
  const t = summary.trim()
  if (!t) return false
  return /\n/.test(t) || /[.?!]["')\]]?\s+\S/.test(t)
}

/** The options as buttons and the box to correct something. Every answer is sent verbatim. */
export function CheckpointActions({ payload, send, disabled, onAnswered, focusFix }: {
  payload: CheckpointCardPayload
  send: (message: string) => Promise<boolean>
  disabled?: boolean
  /** Called once after an answer lands (analytics): a listed option (by its text) or a typed correction. */
  onAnswered?: (how: "option" | "text", option?: string) => void
  /** Picking a "Correct…" option focuses the correction box instead of sending (the box is always there). */
  focusFix?: boolean
}) {
  const isContinue = payload.stage === "continue"
  const options = payload.options.length ? payload.options : isContinue ? [...CONTINUE_OPTIONS] : []
  const [text, setText] = useState("")
  const [busy, setBusy] = useState(false)
  const fixRef = useRef<HTMLTextAreaElement>(null)
  const isCorrect = (o: string) => /^correct/i.test(o)
  const off = disabled || busy
  const answer = async (message: string, how: "option" | "text" = "option"): Promise<boolean> => {
    setBusy(true)
    let ok = false
    try {
      ok = await send(message)
    } catch {
      ok = false
    }
    // A sent answer keeps the card locked (no double submit); only a failed send frees it.
    if (!ok) setBusy(false)
    else onAnswered?.(how, how === "option" ? message : undefined)
    return ok
  }
  const correct = async () => {
    const t = text.trim()
    if (!t || off) return
    if (await answer(t, "text")) setText("")
  }
  return (
    <>
      {options.length ? (
        <div className="nsd-stop-acts" role="group" aria-label="Options">
          {options.map((o, i) => (
            <button key={`${i}-${o}`} type="button" className={`ncf-btn${i === 0 ? " primary" : ""}`} disabled={off} onClick={() => { if (focusFix && isCorrect(o)) fixRef.current?.focus(); else void answer(o) }}>{o}</button>
          ))}
        </div>
      ) : null}
      {isContinue ? null : (
        <form className="nsd-cp-fix" onSubmit={(e) => { e.preventDefault(); void correct() }}>
          <textarea
            className="nsd-in"
            ref={fixRef}
            rows={2}
            aria-label="Correct something"
            placeholder="Correct something…"
            value={text}
            disabled={off}
            onChange={(e) => setText(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey && !e.nativeEvent.isComposing) { e.preventDefault(); void correct() } }}
          />
          <button type="submit" className="ncf-btn" disabled={off || !text.trim()}>Send correction</button>
        </form>
      )}
    </>
  )
}

/** A stage checkpoint as its own card: the agent's summary, then the options and the correction box. Used when the stage's rows are not on the page yet. */
export function CheckpointCard({ engine, payload, send, disabled, onAnswered, landed }: {
  engine: SimEngine
  payload: CheckpointCardPayload
  send: (message: string) => Promise<boolean>
  disabled?: boolean
  /** The stage's rows are already on the page: the agent's note then repeats them, so it folds away. */
  landed?: boolean
  onAnswered?: (how: "option" | "text", option?: string) => void
}) {
  const { stage, summary_markdown: summary } = payload
  const isContinue = stage === "continue"
  const section = engine.sections.find((s) => s.stage === stage)
  const title = isContinue ? "Paused to save progress" : section ? `${section.title} · ready for your check` : stage
  return (
    <section className="nsd-cp" aria-label={`Checkpoint: ${title}`}>
      <b className="nsd-cp-title">{title}</b>
      {stage === "intake" && landed ? (
        <details className="nsd-cp-note">
          <summary>Show the agent's note</summary>
          <Markdown className="nsd-cp-md">{summary}</Markdown>
        </details>
      ) : (
        <Markdown className="nsd-cp-md">{summary}</Markdown>
      )}
      <CheckpointActions payload={payload} send={send} disabled={disabled} onAnswered={onAnswered} />
    </section>
  )
}
